/**
 * Interlock actor store (frontend side of backend utils/interlockActor.ts).
 *
 * The whole agreement layer is multi-identity (party sign · checker countersign ·
 * arbiter ruling) but local-admin god mode is ONE identity. The backend exposes a
 * DEV-ONLY `?actAs=<userId>` escape hatch (ignored the moment JWT_SECRET is set) so
 * a two-party handshake can be exercised without real SSO. This module is the
 * frontend half: it remembers which of the seed engineers we're acting as, appends
 * `actAs` to Interlock API calls, and lets the nav-rail user chip switch identity.
 *
 * Production-safe (P4a-A1): in a production build (`!import.meta.env.DEV`) the
 * switcher never renders (NavRail render-gates on `import.meta.env.DEV`) and this
 * module instead hydrates ONCE from `GET /api/auth/me` (the real Tracer-SSO
 * identity — id/name/role) so every consumer (avatars, composer, workshop driver)
 * reflects the real signed-in user without forking new state — they all just read
 * getActor()/getActorId() as before.
 */
import { useEffect, useState } from 'react';
import { can } from '@fmeastudio/shared';
import { API_BASE_URL } from '../api/config';

export interface InterlockUser {
  id: string; // 'shijin' — the backfilled User.id
  name: string; // 'Shijin Mathews'
  short: string; // 'Shijin'
  initials: string; // 'SH'
  discipline: string; // 'Mechanical'
  role: 'ADMIN' | 'ENGINEER' | 'VIEWER' | 'MANAGER' | 'PROJECT_MANAGER' | 'GUEST';
  // Y5d (round 4) — the dev switcher previously showed only a first name + initials,
  // which is ambiguous the moment two active users share a first name ("two Nicks").
  // Optional: absent for the static dev fixtures below (they don't have real
  // mailboxes), present once the roster comes from the real user table.
  email?: string;
  // Profile avatar (Nick) — a data: URL when the user uploaded one; null/absent
  // → render the initials badge. From /auth/me + /auth/roster.
  avatarUrl?: string | null;
}

// The three PRO-302 engineers seeded by interlockBackfill.ts, plus the local-admin
// god-mode identity (dev-only pick — the lead/manager needs to be pickable per the
// manager-audit finding #0: "the lead can't act as themselves").
//
// ⚠️ 2026-07-29: the 'shijin' / 'alex' / 'james' ids here are DEAD. The DB was
// reseeded with Prisma cuids, so acting as one of them resolves to no user and
// 403s every write. They survive ONLY as the pre-roster placeholder list that
// `switcherUsers()` renders for the ~100ms before `/auth/users` answers; the
// live roster replaces them, and nothing should ever DEFAULT to one of them
// (see FALLBACK_ACTOR / resolveDefaultActorFromRoster below). 'local-admin' is
// the one id in this list that is still real.
export const INTERLOCK_USERS: InterlockUser[] = [
  {
    id: 'shijin',
    name: 'Shijin Mathews',
    short: 'Shijin',
    initials: 'SH',
    discipline: 'Mechanical',
    role: 'ENGINEER',
  },
  {
    id: 'alex',
    name: 'Alex Gravestock',
    short: 'Alex',
    initials: 'AL',
    discipline: 'Electrical',
    role: 'ENGINEER',
  },
  {
    id: 'james',
    name: 'James',
    short: 'James',
    initials: 'JM',
    discipline: 'Software',
    role: 'ENGINEER',
  },
  {
    id: 'local-admin',
    name: 'Lead / Admin (local-admin)',
    short: 'Admin',
    initials: 'LA',
    discipline: 'Leadership',
    role: 'ADMIN',
  },
];

const STORAGE_KEY = 'interlock.actAs';

/**
 * TRAP FIX 2026-07-29 — the dev default must never be a HARDCODED SEED ID.
 *
 * This used to be `'shijin'`, one of the INTERLOCK_USERS fixtures below. Those
 * ids were real `User.id` values only while the DB was seeded by the original
 * `interlockBackfill.ts`. The dev database has since been reseeded with
 * Prisma-generated cuids (`cms4r0cx…`), so `?actAs=shijin` resolves to NO USER,
 * `resolveActorRole` (backend `authz/policy.ts`) returns `null`, and
 * `can(null, 'ENGINEERING_WRITE')` is false — i.e. EVERY write 403'd in dev out
 * of the box, making a working app look broken (three agents lost time to this
 * on 2026-07-28; at least one recorded a false negative).
 *
 * `local-admin` is the correct FALLBACK because it is the one id that cannot
 * rot: `auth.middleware.ts` hardcodes it as the dev god-mode identity,
 * `resolveActorRole`/`isAdminActor`/`isEngineerPlusActor` all short-circuit on
 * it before touching the DB, and the seed also materialises a real `User` row
 * for it (so FK-bearing writes like `recordedById` succeed).
 *
 * It is only the fallback: `resolveDefaultActorFromRoster()` below upgrades the
 * default to a real, LIVE ENGINEER as soon as the roster endpoint answers.
 */
const FALLBACK_ACTOR = 'local-admin';

/**
 * Ids that are always valid regardless of what the DB contains — see above.
 * Anything else must be present in the live roster or it is treated as stale.
 */
const ROT_PROOF_ACTOR_IDS = new Set([FALLBACK_ACTOR]);

const storedActor =
  (typeof localStorage !== 'undefined' && localStorage.getItem(STORAGE_KEY)) || null;

let current = storedActor || FALLBACK_ACTOR;

/**
 * Was the current actor picked DELIBERATELY (URL `?actAs=`) or is it just a
 * default we're free to re-resolve once the live roster lands? An explicit URL
 * pick is never second-guessed — `?actAs=<viewer cuid>` must still 403, and an
 * explicit typo must fail loudly rather than be silently upgraded to a writer.
 */
let actorPinnedByUrl = false;

// R16 Parcel D — honour `?actAs=<id>` in the URL as a convenience for the same
// DEV-ONLY hatch `withActor` below already gates on `import.meta.env.DEV` (the
// hatch this whole module implements — ignored the moment JWT_SECRET is set in
// prod, per the file header). Previously only localStorage was consulted, so
// sharing a link like `/interlock/?actAs=alex` silently did nothing — the page
// still acted as whatever was last in localStorage. Seed both the in-memory
// actor and localStorage so subsequent renders/navigation/API calls (all of
// which read `current` or localStorage) pick it up consistently. Reuses the
// exact same guard as `withActor` — this MUST be a strict no-op in prod.
if (import.meta.env.DEV && typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
  try {
    const urlActAs = new URLSearchParams(window.location.search).get('actAs');
    if (urlActAs) {
      current = urlActAs;
      actorPinnedByUrl = true;
      localStorage.setItem(STORAGE_KEY, urlActAs);
    }
  } catch {
    /* malformed URL/search — fall back to whatever localStorage/default gave us */
  }
}

// Production hydration target: the real signed-in user, once `/api/auth/me` resolves.
// Not one of INTERLOCK_USERS (that list is dev-only fixtures) — kept separate so the
// dev switcher's fixed roster is never polluted by a live SSO identity.
let hydratedUser: InterlockUser | null = null;
let hydrationStarted = false;

function initialsFrom(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '??';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** Production-only, one-shot: hydrate the real identity from GET /api/auth/me. */
async function hydrateFromAuthMe(): Promise<void> {
  if (hydrationStarted) return;
  hydrationStarted = true;
  try {
    const res = await fetch(`${API_BASE_URL}/auth/me`, { credentials: 'include' });
    if (!res.ok) return;
    const body = await res.json();
    const u = body?.data as
      | { id?: string; name?: string; email?: string; role?: string; avatarUrl?: string | null }
      | undefined;
    if (!u?.id) return;
    const name = u.name || u.email || u.id;
    hydratedUser = {
      id: u.id,
      name,
      short: name.split(/\s+/)[0] || name,
      initials: initialsFrom(name),
      discipline: '',
      role: (u.role as InterlockUser['role']) ?? 'ENGINEER',
      email: u.email,
      avatarUrl: u.avatarUrl ?? null,
    };
    current = u.id;
    for (const fn of listeners) fn(current);
  } catch {
    /* best-effort — falls back to the dev-fixture actor if /auth/me is unreachable */
  }
}

if (typeof window !== 'undefined' && !import.meta.env.DEV) {
  void hydrateFromAuthMe();
}

const listeners = new Set<(id: string) => void>();

export function getActorId(): string {
  return current;
}

export function getActor(): InterlockUser {
  if (hydratedUser && hydratedUser.id === current) return hydratedUser;
  const dyn = dynamicUsers.find((u) => u.id === current);
  if (dyn) return dyn;
  return (
    INTERLOCK_USERS.find((u) => u.id === current) ??
    // Last-resort identity is the rot-proof local-admin, NOT INTERLOCK_USERS[0]
    // ('shijin' — a dead seed id whose every write 403s; see FALLBACK_ACTOR).
    INTERLOCK_USERS.find((u) => u.id === FALLBACK_ACTOR) ??
    INTERLOCK_USERS[0]
  );
}

/**
 * React hook — the SOURCE fix for the getActor() fallback race (#199 follow-up).
 *
 * A bare `getActor()` call resolves synchronously against whatever roster has
 * loaded by the time of that render. Called before `fetchInterlockUsers()` /
 * `hydrateFromAuthMe()` resolve (the common case: they're async and consumers
 * render immediately), it silently returns the `INTERLOCK_USERS[0]` ('shijin')
 * fallback — and because a plain function call isn't reactive, any consumer
 * that read it once (AdminPage's `isAdmin` gate, its "you're signed in as…"
 * denial label, capability checks on the interface/trade detail pages) stayed
 * wrong even after the roster/hydration resolved, misattributing identity to
 * "Shijin" for everyone. `subscribeActor` already notifies on every actor
 * change AND on roster-ready/hydration-complete (see `fetchInterlockUsers` /
 * `hydrateFromAuthMe` below — both fire listeners unconditionally, not only on
 * an id change), so the fix is to actually listen: recompute on every
 * notification, and kick the roster fetch ourselves so a lone consumer
 * doesn't depend on some other component (e.g. NavRail) having mounted first
 * and triggered it. Same pattern SettingsPage hand-rolled locally; this is the
 * shared, reusable version other identity-reading components should use
 * instead of a bare `getActor()`.
 */
export function useActor(): InterlockUser {
  const [actor, setActor] = useState<InterlockUser>(() => getActor());
  useEffect(() => {
    const recompute = () => setActor(getActor());
    const unsubscribe = subscribeActor(recompute);
    fetchInterlockUsers().then(recompute);
    // Also recompute once eagerly — hydrateFromAuthMe() may already be
    // in-flight (kicked at module load in prod) and could resolve between
    // this component's initial getActor() call and this effect running.
    recompute();
    return unsubscribe;
  }, []);
  return actor;
}

export function userById(id: string | null | undefined): InterlockUser | undefined {
  if (!id) return undefined;
  if (hydratedUser && hydratedUser.id === id) return hydratedUser;
  const dyn = dynamicUsers.find((u) => u.id === id);
  if (dyn) return dyn;
  return INTERLOCK_USERS.find((u) => u.id === id);
}

// ---------------------------------------------------------------------------
// X9b — dynamic roster (dev switcher + prod avatars/typeahead).
//
// DEV: the switcher should list ALL active human users (the real driver/Nick
// included), not just the three hardcoded seed engineers + local-admin.
// Source: GET /api/auth/users (auth.routes.ts — admin only, but dev mode is
// local-admin god mode per auth.middleware.ts so this always succeeds when
// JWT_SECRET is unset). The admin endpoint also carries email, which the
// switcher renders to disambiguate two users sharing a first name (Y5d).
//
// PROD (punch-list batch 2, #147/#151): previously this module returned the
// static INTERLOCK_USERS fixtures in prod builds, so the @-mention typeahead
// could suggest names that resolve to nobody and real SSO users' avatars
// degraded to userId.slice(0,2). Now it fetches GET /api/auth/roster — the
// any-authed-user, id/name/role-only cut of the same list (no emails; the
// admin-gated /users endpoint keeps the richer shape for the dev switcher
// and AdminPage).
//
// Both paths: fetched once, lazily, on first call; falls back to the static
// INTERLOCK_USERS fixtures if the fetch fails so callers are never empty.
let dynamicUsers: InterlockUser[] = [];
let dynamicUsersState: 'idle' | 'loading' | 'ready' | 'error' = 'idle';
let dynamicUsersPromise: Promise<InterlockUser[]> | null = null;

function roleOf(raw: unknown): InterlockUser['role'] {
  const v = String(raw ?? '').toUpperCase();
  return v === 'ADMIN' ||
    v === 'ENGINEER' ||
    v === 'VIEWER' ||
    v === 'MANAGER' ||
    v === 'PROJECT_MANAGER' ||
    v === 'GUEST'
    ? v
    : 'ENGINEER';
}

/**
 * TRAP FIX 2026-07-29 — resolve the DEV default actor from the LIVE roster.
 *
 * A hardcoded default id rots the moment the DB is reseeded (see FALLBACK_ACTOR
 * above). So we don't pin one: once `/auth/users` answers, if the actor is only
 * a default (or a STALE id left in localStorage by a pre-reseed session — the
 * `interlock.actAs=shijin` every agent's browser profile is still carrying), we
 * adopt the first live user who actually holds ENGINEERING_WRITE, preferring a
 * plain ENGINEER so the default exercises the ordinary non-admin path the old
 * 'shijin' default did.
 *
 * Deliberately NOT persisted to localStorage: persisting would re-create
 * exactly the pinned-id failure this fixes. The default re-resolves every page
 * load; only a switcher pick (`setActorId`) or `?actAs=` is ever written.
 *
 * Never overrides an explicit URL `?actAs=` — a viewer cuid must still 403 and
 * a typo must fail loudly. Guarded by `utils/__tests__/interlockActor.default.test.ts`.
 */
function resolveDefaultActorFromRoster(users: InterlockUser[]): void {
  if (actorPinnedByUrl) return;
  const storedIsLive =
    !!storedActor &&
    (users.some((u) => u.id === storedActor) || ROT_PROOF_ACTOR_IDS.has(storedActor));
  // A stored id that IS live is a real switcher pick — leave it alone.
  if (storedIsLive && storedActor === current) return;
  const writers = users.filter((u) => can(u.role, 'ENGINEERING_WRITE'));
  const pick = writers.find((u) => u.role === 'ENGINEER') ?? writers[0];
  if (!pick || pick.id === current) return;
  if (storedActor && !storedIsLive) {
    console.warn(
      `[interlockActor] stored actor "${storedActor}" is not in the live roster ` +
        `(stale seed id — the DB was reseeded); defaulting to "${pick.id}" (${pick.name})`
    );
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* storage may be unavailable — in-memory is enough */
    }
  }
  current = pick.id;
}

/**
 * Fetch the real active-user roster (idempotent — only fetches once per page
 * load; call again after an error to retry). DEV → admin /auth/users (richer:
 * email for the switcher); PROD → any-authed /auth/roster (id/name/role).
 * Returns the static fixtures on any failure (network error, non-2xx) so
 * callers can render unconditionally.
 */
export function fetchInterlockUsers(): Promise<InterlockUser[]> {
  if (dynamicUsersState === 'ready') return Promise.resolve(dynamicUsers);
  if (dynamicUsersPromise) return dynamicUsersPromise;
  const rosterPath = import.meta.env.DEV ? '/auth/users' : '/auth/roster';
  dynamicUsersState = 'loading';
  dynamicUsersPromise = fetch(`${API_BASE_URL}${rosterPath}`, { credentials: 'include' })
    .then(async (res) => {
      if (!res.ok) throw new Error(`${rosterPath} ${res.status}`);
      const body = await res.json();
      const rows = Array.isArray(body?.data) ? body.data : [];
      if (!rows.length) throw new Error('empty roster');
      const users: InterlockUser[] = rows.map(
        (u: {
          id: string;
          name?: string;
          email?: string;
          role?: string;
          avatarUrl?: string | null;
        }) => {
          const name = u.name || u.email || u.id;
          return {
            id: u.id,
            name,
            short: name.split(/\s+/)[0] || name,
            initials: initialsFrom(name),
            discipline: '',
            role: roleOf(u.role),
            email: u.email,
            avatarUrl: u.avatarUrl ?? null,
          };
        }
      );
      dynamicUsers = users;
      dynamicUsersState = 'ready';
      resolveDefaultActorFromRoster(users);
      for (const fn of listeners) fn(current);
      return users;
    })
    .catch((err) => {
      // PR#155 review (LOW) — never fall back silently: name the endpoint that
      // failed so a broken roster fetch is visible instead of masquerading as
      // a working actor switcher full of fixture users.
      console.warn(
        `[interlockActor] roster fetch ${API_BASE_URL}${rosterPath} failed (${
          err instanceof Error ? err.message : String(err)
        }) — showing fixture users`
      );
      dynamicUsersState = 'error';
      dynamicUsersPromise = null; // allow a future retry
      return INTERLOCK_USERS;
    });
  return dynamicUsersPromise;
}

/** Synchronous read of whatever roster is currently loaded (fixtures until the
 * dev fetch resolves) — for render paths that can't await. */
export function switcherUsers(): InterlockUser[] {
  return dynamicUsersState === 'ready' ? dynamicUsers : INTERLOCK_USERS;
}

export function setActorId(id: string): void {
  if (id === current) return;
  current = id;
  try {
    localStorage.setItem(STORAGE_KEY, id);
  } catch {
    /* storage may be unavailable — in-memory is enough */
  }
  for (const fn of listeners) fn(id);
}

/**
 * Update the current actor's avatar in-place (after a profile upload/remove) and
 * notify subscribers, so the nav badge + profile card re-render immediately
 * without a page reload. Patches whichever source getActor() reads from.
 */
export function setActorAvatar(url: string | null): void {
  if (hydratedUser && hydratedUser.id === current) hydratedUser.avatarUrl = url;
  const dyn = dynamicUsers.find((u) => u.id === current);
  if (dyn) dyn.avatarUrl = url;
  for (const fn of listeners) fn(current);
}

/** Subscribe to actor changes; returns an unsubscribe fn (React-effect friendly). */
export function subscribeActor(fn: (id: string) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Append `?actAs=<id>` (or `&actAs=`) to an API path in dev; no-op if disabled. */
export function withActor(path: string, actorId: string = current): string {
  if (!import.meta.env.DEV) return path;
  const sep = path.includes('?') ? '&' : '?';
  return `${path}${sep}actAs=${encodeURIComponent(actorId)}`;
}
